<template>
    <div class="h-full">
        <!-- Drop zone -->
        <div
            class="relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors cursor-pointer"
            :class="
                dragOver
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-950'
                    : 'border-gray-300 hover:border-gray-400 dark:border-zinc-600 dark:hover:border-zinc-500'
            "
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="onDrop"
            @click="openFileDialog"
        >
            <template v-if="analyzing">
                <div class="flex flex-col items-center gap-3">
                    <div class="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-brand-500"></div>
                    <span class="text-sm font-medium text-gray-600 dark:text-zinc-300">
                        {{ lang('invoices::analysis.widget.analyzing') }}
                    </span>
                </div>
            </template>
            <template v-else-if="error">
                <div class="flex flex-col items-center gap-2 text-center">
                    <icon icon="hero/outline/exclamation-triangle" class="h-8 w-8 text-red-500" />
                    <span class="text-sm text-red-600 dark:text-red-400">{{ error }}</span>
                    <button class="text-xs text-brand-600 hover:underline" @click.stop="resetState">
                        {{ lang('invoices::analysis.widget.try_again') }}
                    </button>
                </div>
            </template>
            <template v-else>
                <icon icon="hero/outline/document-arrow-up" class="mb-2 h-8 w-8 text-gray-400 dark:text-zinc-500" />
                <span class="text-sm font-medium text-gray-600 dark:text-zinc-300">
                    {{ lang('invoices::analysis.widget.drop_or_click') }}
                </span>
                <span class="mt-1 text-xs text-gray-400 dark:text-zinc-500"> PDF, JPG, PNG, ISDOC </span>
            </template>
        </div>

        <input ref="fileInput" type="file" class="hidden" :accept="acceptedTypes" @change="onFileSelect" />

        <!-- Analysis results modal -->
        <InvoiceAnalysisModal
            v-if="analysisResult"
            :key="analysisResult.parse_log.id"
            :visible="modalVisible"
            :mapped-data="analysisResult.mapped_data"
            :parse-log="analysisResult.parse_log"
            :create-url="createUrl"
            @close="closeModal"
        />
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { lang } from '@/lang/composable';
import axios from 'axios';
import InvoiceAnalysisModal from '@src/Invoices/Pages/Components/InvoiceAnalysisModal.vue';

interface Props {
    settings: Record<string, unknown>;
    slug: string;
}

const props = defineProps<Props>();

const dragOver = ref(false);
const analyzing = ref(false);
const error = ref<string | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const modalVisible = ref(false);
interface AnalysisResponse {
    success: boolean;
    mapped_data: Record<string, unknown>;
    parse_log: { id: number; [key: string]: unknown };
    [key: string]: unknown;
}

const analysisResult = ref<AnalysisResponse | null>(null);

// URLs from widget data
const analyzeUrl = ref('');
const createUrl = ref('');
const acceptedTypes = ref('.pdf,.jpg,.jpeg,.png,.isdoc,.isdocx,.xml');

onMounted(async () => {
    try {
        const response = await axios.get(route('admin.dashboards.widgets.data', { slug: props.slug }));
        analyzeUrl.value = response.data.analyzeUrl;
        createUrl.value = response.data.createUrl;
        acceptedTypes.value = response.data.acceptedTypes || acceptedTypes.value;
    } catch {
        // URLs will be resolved from route helper as fallback
        analyzeUrl.value = route('invoices.admin.analyze');
        createUrl.value = route('invoices.admin.create-from-analysis');
    }
});

const openFileDialog = () => {
    if (!analyzing.value) {
        fileInput.value?.click();
    }
};

const onDrop = (event: DragEvent) => {
    dragOver.value = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
        uploadFile(files[0]);
    }
};

const onFileSelect = (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
        uploadFile(input.files[0]);
        input.value = '';
    }
};

const uploadFile = async (file: File) => {
    analyzing.value = true;
    error.value = null;

    const formData = new FormData();
    formData.append('file', file);

    try {
        const url = analyzeUrl.value || route('invoices.admin.analyze');
        const response = await axios.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data.success) {
            analysisResult.value = response.data;
            modalVisible.value = true;
        } else {
            error.value = response.data.error || lang('invoices::analysis.widget.analysis_failed');
        }
    } catch (err: unknown) {
        const axiosError = err as { response?: { data?: { error?: string; message?: string } } };
        error.value =
            axiosError.response?.data?.error ||
            axiosError.response?.data?.message ||
            lang('invoices::analysis.widget.analysis_failed');
    } finally {
        analyzing.value = false;
    }
};

const closeModal = () => {
    modalVisible.value = false;
};

const resetState = () => {
    error.value = null;
    analysisResult.value = null;
};
</script>
